import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Build as ProductionIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import productionOrderService from '../../../../services/productionOrderService';
import recipeService from '../../../../services/recipeService';
import type { ProductionOrder, ProductionOrderStatus, Recipe } from '../../../../types/venue';

const STATUS_CFG: Record<ProductionOrderStatus, { label: string; color: string; bg: string }> = {
  DRAFT:  { label: 'Draft',  color: '#9a6700', bg: '#fef9c3' },
  POSTED: { label: 'Posted', color: '#1a7f37', bg: '#dafbe1' },
};

const ProductionStatusBadge: React.FC<{ status: ProductionOrderStatus }> = ({ status }) => {
  const cfg = STATUS_CFG[status] ?? { label: status, color: '#6e7781', bg: '#f6f8fa' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 600, fontSize: '0.7rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 22 }}
    />
  );
};

const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ProductionOrderList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}/production-orders`;

  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<ProductionOrderStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipeMap, setRecipeMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!venueId) return;
    recipeService.listRecipes(venueId, { pageSize: 200 })
      .then(r => setRecipeMap(new Map(r.data.map((rc: Recipe) => [rc._id, rc.name]))))
      .catch(() => {});
  }, [venueId]);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await productionOrderService.listProductionOrders(venueId, {
        page: page + 1,
        pageSize,
        status: statusFilter || undefined,
      });
      setOrders(result.data);
      setTotal(result.pagination.totalItems);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load production orders');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          flexWrap: 'wrap', gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ProductionIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Production Orders</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value as ProductionOrderStatus | ''); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="POSTED">Posted</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`${base}/create`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              New Order
            </Button>
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Loading production orders…</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Order', 'Recipe', 'Planned Qty', 'Status', 'Planned Date', 'Created'].map(h => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.72rem', textTransform: 'uppercase' }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <ProductionIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">
                            {statusFilter ? 'No orders match the current filter.' : 'No production orders yet.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map(order => (
                      <TableRow
                        key={order._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`${base}/${order._id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.78rem">
                            {order._id.slice(-8).toUpperCase()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {recipeMap.has(order.recipeId)
                              ? recipeMap.get(order.recipeId)
                              : <span style={{ color: theme.palette.text.disabled, fontFamily: 'monospace', fontSize: '0.72rem' }}>{order.recipeId.slice(-8)}</span>}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.plannedQuantity}</Typography>
                        </TableCell>
                        <TableCell>
                          <ProductionStatusBadge status={order.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatDate(order.plannedDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatDate(order.createdAt)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProductionOrderList;
